import { useState } from "react";
import { cn } from "@/lib/utils";
import { useClient } from "@/contexts/ClientContext";

/**
 * BrandTile — the venue logo treated like an app icon.
 *
 * Every tenant uploads a different kind of logo (square badge, wide wordmark,
 * storefront photo…). A rounded tile with a white face shows all of them
 * cleanly: square-ish images fill the tile, wide ones are contained with a
 * little breathing room. Without a logo, the tile shows the venue's initial on
 * the brand color, so the brand still leads.
 */
type TileSize = "xs" | "sm" | "md" | "lg" | "xl";

const SIZES: Record<TileSize, { px: number; radius: string; font: string }> = {
  xs: { px: 32, radius: "rounded-lg", font: "text-sm" },
  sm: { px: 44, radius: "rounded-xl", font: "text-lg" },
  md: { px: 56, radius: "rounded-2xl", font: "text-2xl" },
  lg: { px: 80, radius: "rounded-[22px]", font: "text-3xl" },
  xl: { px: 108, radius: "rounded-[30px]", font: "text-5xl" },
};

interface BrandTileProps {
  size?: TileSize;
  className?: string;
  /** Override the logo (defaults to the current client's). Pass null for none. */
  logoUrl?: string | null;
  /** Override the name used for the alt text and the monogram fallback. */
  name?: string | null;
  /** Play the "tile lands" entrance. */
  animate?: boolean;
  /** Decorative duplicate of a name shown right next to it: hide from readers. */
  decorative?: boolean;
}

export const BrandTile = ({
  size = "md",
  className,
  logoUrl,
  name,
  animate = false,
  decorative = false,
}: BrandTileProps) => {
  const { client } = useClient();
  const src = logoUrl === undefined ? client?.logo_url : logoUrl;
  const label = (name ?? client?.name ?? "").trim();
  const [fit, setFit] = useState<"cover" | "contain">("cover");
  const [broken, setBroken] = useState(false);

  const s = SIZES[size];
  const showImage = !!src && !broken;
  const initial = label.charAt(0).toUpperCase() || "•";

  return (
    <div
      className={cn(
        "brand-tile",
        s.radius,
        showImage ? `brand-tile--${fit}` : "brand-tile--mono",
        animate && "pt-tile",
        className,
      )}
      style={{ width: s.px, height: s.px }}
      aria-hidden={decorative || undefined}
    >
      {showImage ? (
        <img
          src={src as string}
          alt={decorative ? "" : label}
          draggable={false}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setFit(Math.abs(w / h - 1) < 0.2 ? "cover" : "contain");
          }}
          onError={() => setBroken(true)}
        />
      ) : (
        <span className={cn("select-none leading-none", s.font)} aria-hidden="true">
          {initial}
        </span>
      )}
    </div>
  );
};

export default BrandTile;
